import { WEEKDAYS, type Weekday } from './validation.ts';

/** A calendar date in the tenant's timezone, "YYYY-MM-DD". */
export type DateKey = string;

const partsCache = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsCache.set(tz, f);
  }
  return f;
}

function zonedParts(date: Date, tz: string) {
  const parts = partsFormatter(tz).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour'), min: get('minute'), s: get('second') };
}

/** Offset of `tz` from UTC at the given instant, in ms (e.g. +2h for Berlin in summer). */
export function tzOffsetMs(date: Date, tz: string): number {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/** Converts a wall-clock time (date + minutes since midnight) in `tz` to a UTC instant. */
export function zonedToUtc(date: DateKey, minutes: number, tz: string): Date {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d, 0, minutes);
  const off1 = tzOffsetMs(new Date(guess), tz);
  let result = guess - off1;
  const off2 = tzOffsetMs(new Date(result), tz);
  if (off2 !== off1) result = guess - off2; // crossed a DST boundary
  return new Date(result);
}

export function dateKeyInTz(date: Date, tz: string): DateKey {
  const p = zonedParts(date, tz);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

export function minutesInTz(date: Date, tz: string): number {
  const p = zonedParts(date, tz);
  return p.h * 60 + p.min;
}

export function addDays(date: DateKey, n: number): DateKey {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export function weekdayOf(date: DateKey): Weekday {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

/** "09:30" -> 570 */
export function parseHHMM(v: string): number {
  const [h, m] = v.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/** "09:00-18:00" -> [540, 1080]; "closed" -> null */
export function parseHours(v: string): [number, number] | null {
  if (v === 'closed') return null;
  const [open, close] = v.split('-') as [string, string];
  return [parseHHMM(open), parseHHMM(close)];
}

export function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
